import { createAsyncThunk } from '@reduxjs/toolkit'

import { formatMessage } from '../../locales/locale.js'
import { trimStreetData } from '../../streets/data_model.js'
import { isOwnedByCurrentUser } from '../../streets/owner.js'
import { finishUndoOrRedo } from '../../streets/undo_stack.js'
import { redo, undo } from '../slices/history.js'
import { addToast } from '../slices/toasts.js'

import type { Dispatch, RootState } from '../index.js'

// These async thunks remain in a separate module because they import other
// modules with deeply nested dependencies ... some of which will throw
// errors when imported too early (when the reducers and slices are being
// created, during app initialization.
// So we can't define these actions alongside the slice.

export const handleUndo = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: Dispatch }
>('history/handleUndo', async (arg, { dispatch, getState }) => {
  const { position } = getState().history
  const { street } = getState()

  // Don't allow undo/redo unless you own the street
  if (position > 0 && isOwnedByCurrentUser()) {
    // Before undoing, send a copy of the current street data
    // to update data at the current position
    dispatch(undo(trimStreetData(street)))
    finishUndoOrRedo()
  } else {
    dispatch(
      addToast({
        message: formatMessage('toast.no-undo', 'Nothing to undo.'),
        duration: 4000,
      })
    )
  }
})

export const handleRedo = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: Dispatch }
>('history/handleRedo', async (arg, { dispatch, getState }) => {
  const { position, stack } = getState().history

  // Don't allow undo/redo unless you own the street
  if (position >= 0 && position < stack.length - 1 && isOwnedByCurrentUser()) {
    dispatch(redo())
    finishUndoOrRedo()
  } else {
    dispatch(
      addToast({
        message: formatMessage('toast.no-redo', 'Nothing to redo.'),
        duration: 4000,
      })
    )
  }
})
